/**
 * API client for The Combine backend
 * Wired to actual FastAPI endpoints
 */

const API_BASE = '/api/v1';

class ApiError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

/**
 * Get CSRF token from cookies
 */
function getCsrfToken() {
    const match = document.cookie.match(/(?:^|;\s*)csrf=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
}

async function request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
        credentials: 'same-origin', // Include cookies
        ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new ApiError(
            data.detail || data.message || `Request failed: ${response.status}`,
            response.status,
            data
        );
    }

    // Handle 204 No Content
    if (response.status === 204) {
        return null;
    }

    return response.json();
}

/**
 * Request with CSRF token (for POST/DELETE that need it)
 */
async function requestWithCsrf(endpoint, options = {}) {
    const csrfToken = getCsrfToken();
    return request(endpoint, {
        ...options,
        headers: {
            ...options.headers,
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
    });
}

export const api = {
    // Authentication
    // Note: /me is at /api/me (not /api/v1/me)
    getMe: () => fetch('/api/me', { credentials: 'same-origin' })
        .then(res => {
            if (res.status === 401) return null;
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            return res.json();
        })
        .catch(err => {
            console.error('Auth check failed:', err);
            return null;
        }),

    logout: () => fetch('/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'X-CSRF-Token': getCsrfToken() || '',
        },
    }),

    // Projects
    getProjects: (params = {}) => {
        const query = new URLSearchParams();
        if (params.search) query.set('search', params.search);
        if (params.includeArchived) query.set('include_archived', 'true');
        const qs = query.toString();
        return request(`/projects${qs ? '?' + qs : ''}`);
    },

    getProject: (projectId) => request(`/projects/${projectId}`),

    getProjectTree: (projectId) => request(`/projects/${projectId}/tree`),

    createProject: (data) => request('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    updateProject: (projectId, data) => request(`/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),

    saveFloorLayout: (projectId, layout) => request(`/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify({ metadata: { floor_layout: layout } }),
    }),

    archiveProject: (projectId) => request(`/projects/${projectId}/archive`, {
        method: 'POST',
    }),

    unarchiveProject: (projectId) => request(`/projects/${projectId}/unarchive`, {
        method: 'POST',
    }),

    deleteProject: (projectId) => request(`/projects/${projectId}`, {
        method: 'DELETE',
    }),

    // Production line
    getProductionStatus: (projectId) =>
        request(`/production/status?project_id=${projectId}`),

    startProduction: (projectId, documentType = null) => {
        const query = new URLSearchParams({ project_id: projectId });
        if (documentType) query.set('document_type', documentType);
        return request(`/production/start?${query}`, { method: 'POST' });
    },

    // Interrupts (operator questions)
    getProjectInterrupts: (projectId) =>
        request(`/projects/${projectId}/interrupts`),

    resolveInterrupt: (interruptId, data) =>
        request(`/interrupts/${interruptId}/resolve`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // Executions (admin monitoring)
    getExecutions: () =>
        request('/executions'),

    getExecution: (executionId) =>
        request(`/executions/${executionId}`),

    getExecutionTranscript: (executionId) =>
        request(`/executions/${executionId}/transcript`),

    getExecutionQACoverage: (executionId) =>
        request(`/executions/${executionId}/qa-coverage`),

    getDocumentWorkflowExecutions: () =>
        request('/document-workflows/executions'),

    getDocumentWorkflowExecution: (executionId) =>
        request(`/document-workflows/executions/${executionId}`),

    // Document workflows
    startWorkflow: (projectId, documentType, initialContext = null) =>
        request('/document-workflows/start', {
            method: 'POST',
            body: JSON.stringify({
                project_id: projectId,
                document_type: documentType,
                initial_context: initialContext,
            }),
        }),

    submitWorkflowInput: (executionId, userInput, selectedOptionId = null) =>
        request(`/document-workflows/executions/${executionId}/input`, {
            method: 'POST',
            body: JSON.stringify({
                user_input: userInput,
                selected_option_id: selectedOptionId,
            }),
        }),

    getExecutionStatus: (executionId) =>
        request(`/document-workflows/executions/${executionId}`),

    getPgcAnswers: (executionId) =>
        request(`/document-workflows/executions/${executionId}/pgc-answers`),

    // Documents
    getDocument: (projectId, docTypeId, instanceId) => {
        const qs = instanceId ? `?instance_id=${encodeURIComponent(instanceId)}` : '';
        return request(`/projects/${projectId}/documents/${docTypeId}${qs}`);
    },

    // Document resolver (ADR-055: by display_id like PD-001, WP-001)
    getDocumentByDisplayId: (projectId, displayId) =>
        request(`/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(displayId)}`),

    // Render document to Markdown (WS-RENDER-001/003/004)
    renderDocument: (projectId, displayId, { profile = 'standard', mode = 'standard' } = {}) =>
        fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(displayId)}/render?format=md&profile=${encodeURIComponent(profile)}&mode=${encodeURIComponent(mode)}`, {
            credentials: 'same-origin',
        }).then(async r => {
            if (r.status === 409) {
                const data = await r.json();
                throw new ApiError(data.message || 'IA verification failed', 409, data);
            }
            if (!r.ok) throw new Error(`Render failed: ${r.status}`);
            return r.blob();
        }),

    // Render project binder to Markdown (WS-RENDER-002/003/004)
    renderProjectBinder: (projectId, { profile = 'print', mode = 'standard' } = {}) =>
        fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/render?scope=project&format=md&profile=${encodeURIComponent(profile)}&mode=${encodeURIComponent(mode)}`, {
            credentials: 'same-origin',
        }).then(async r => {
            if (r.status === 409) {
                const data = await r.json();
                throw new ApiError(data.message || 'IA verification failed', 409, data);
            }
            if (!r.ok) throw new Error(`Binder render failed: ${r.status}`);
            return r.blob();
        }),

    // RenderModel (data-driven document display)
    getDocumentRenderModel: (projectId, docTypeId, instanceId) => {
        const qs = instanceId ? `?instance_id=${encodeURIComponent(instanceId)}` : '';
        return request(`/projects/${projectId}/documents/${docTypeId}/render-model${qs}`);
    },

    // PGC context (questions, rationale, answers)
    getDocumentPgc: (projectId, docTypeId, instanceId) => {
        const qs = instanceId ? `?instance_id=${encodeURIComponent(instanceId)}` : '';
        return request(`/projects/${projectId}/documents/${docTypeId}/pgc${qs}`);
    },

    // Work Packages
    getWorkPackages: (projectId) =>
        request(`/projects/${projectId}/work-packages`),

    generateWorkPackages: (projectId) =>
        request(`/projects/${projectId}/work-packages/generate`, { method: 'POST' }),

    getWorkStatements: (projectId, wpId) =>
        request(`/projects/${projectId}/work-packages/${wpId}/work-statements`),

    generateWorkStatements: (projectId, wpId) =>
        request(`/projects/${projectId}/work-packages/${wpId}/work-statements/generate`, { method: 'POST' }),

    // Work Binder — Candidates
    getCandidates: (projectId) =>
        request(`/work-binder/candidates?project_id=${encodeURIComponent(projectId)}`),

    importCandidates: (ipDocumentId) =>
        request('/work-binder/import-candidates', {
            method: 'POST',
            body: JSON.stringify({ ip_document_id: ipDocumentId }),
        }),

    promoteCandidate: (wpcId, projectId, transformation = 'kept', transformationNotes = '') =>
        request('/work-binder/promote', {
            method: 'POST',
            body: JSON.stringify({
                wpc_id: wpcId,
                project_id: projectId,
                transformation,
                transformation_notes: transformationNotes,
            }),
        }),

    getWorkPackageDetail: (wpId, projectId = null) => {
        const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
        return request(`/work-binder/wp/${encodeURIComponent(wpId)}${qs}`);
    },

    proposeWorkStatements: (projectId, wpId) =>
        request('/work-binder/propose-ws', {
            method: 'POST',
            body: JSON.stringify({ project_id: projectId, wp_id: wpId }),
        }),

    // Concierge Intake
    startIntake: () =>
        request('/intake/start', { method: 'POST' }),

    getIntakeState: (executionId) =>
        request(`/intake/${executionId}`),

    submitIntakeMessage: (executionId, content) =>
        request(`/intake/${executionId}/message`, {
            method: 'POST',
            body: JSON.stringify({ content }),
        }),

    updateIntakeField: (executionId, fieldKey, value) =>
        request(`/intake/${executionId}/field/${fieldKey}`, {
            method: 'PATCH',
            body: JSON.stringify({ value }),
        }),

    initializeIntake: (executionId) =>
        request(`/intake/${executionId}/initialize`, { method: 'POST' }),

    // Workflow Instances (ADR-046)
    getWorkflowInstance: (projectId) =>
        request(`/projects/${projectId}/workflow`),

    createWorkflowInstance: (projectId, workflowId, version) =>
        request(`/projects/${projectId}/workflow`, {
            method: 'POST',
            body: JSON.stringify({ workflow_id: workflowId, version }),
        }),

    updateWorkflowInstance: (projectId, effectiveWorkflow) =>
        request(`/projects/${projectId}/workflow`, {
            method: 'PUT',
            body: JSON.stringify({ effective_workflow: effectiveWorkflow }),
        }),

    getWorkflowDrift: (projectId) =>
        request(`/projects/${projectId}/workflow/drift`),

    getWorkflowHistory: (projectId, limit = 50, offset = 0) =>
        request(`/projects/${projectId}/workflow/history?limit=${limit}&offset=${offset}`),

    completeWorkflowInstance: (projectId) =>
        request(`/projects/${projectId}/workflow/complete`, { method: 'POST' }),

    // Intent Intake (WS-BCP-001)
    createIntent: (projectId, data) =>
        request('/intents', {
            method: 'POST',
            body: JSON.stringify({ project_id: projectId, ...data }),
        }),

    getIntent: (intentId) =>
        request(`/intents/${intentId}`),

    // ---- Pipeline Rewind (ADR-063) ----

    rewindPipeline: (projectId, stage, reason, actor = 'user', appliesTo = null) =>
        request(`/projects/${projectId}/rewind`, {
            method: 'POST',
            body: JSON.stringify({
                rewind_to_stage: stage,
                reason,
                actor,
                ...(appliesTo ? { applies_to_stages: appliesTo } : {}),
            }),
        }),

    getCorrection: (projectId, stage) =>
        request(`/projects/${projectId}/corrections/${stage}`).catch(err => {
            // 404 is expected when no correction exists
            if (err?.status === 404 || err?.message?.includes('404') || err?.message?.includes('No correction')) return null;
            // Don't throw on other errors — pre-population is best-effort
            console.warn('getCorrection failed:', err);
            return null;
        }),

    updateCorrection: (projectId, stage, text, appliesTo = null) =>
        request(`/projects/${projectId}/corrections/${stage}`, {
            method: 'PATCH',
            body: JSON.stringify({
                text,
                ...(appliesTo ? { applies_to_stages: appliesTo } : {}),
            }),
        }),

    validateRegeneration: (projectId, fromStage, actor = 'user') =>
        request(`/projects/${projectId}/regenerate/validate`, {
            method: 'POST',
            body: JSON.stringify({
                from_stage: fromStage,
                actor,
            }),
        }),

    getLineage: (projectId, eventType = null) =>
        request(`/projects/${projectId}/lineage${eventType ? `?event_type=${eventType}` : ''}`),

    getPipelineStatus: (projectId) =>
        request(`/projects/${projectId}/pipeline-status`),

    getTimeline: (projectId) =>
        request(`/projects/${projectId}/timeline`),

    getProjectTimeline: (projectId) =>
        request(`/projects/${projectId}/timeline`),

    restoreCheckpoint: (projectId, checkpointId, reason = 'Operator restore') =>
        request(`/projects/${projectId}/restore`, {
            method: 'POST',
            body: JSON.stringify({ checkpoint_id: checkpointId, reason }),
        }),

    archiveThread: (projectId, eventId, reason = 'Operator archive') =>
        request(`/projects/${projectId}/archive-thread`, {
            method: 'POST',
            body: JSON.stringify({ event_id: eventId, reason }),
        }),

    // Artifacts (project mockups/attachments)
    uploadArtifact: (projectId, file, label) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('label', label);
        return fetch(`${API_BASE}/projects/${projectId}/artifacts`, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        }).then(async (res) => {
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new ApiError(
                    data.detail || data.message || `Upload failed: ${res.status}`,
                    res.status,
                    data
                );
            }
            return res.json();
        });
    },

    listArtifacts: (projectId) =>
        request(`/projects/${projectId}/artifacts`),

    deleteArtifact: (projectId, artifactId) =>
        request(`/projects/${projectId}/artifacts/${artifactId}`, {
            method: 'DELETE',
        }),

    updateArtifact: (projectId, artifactId, updates) =>
        request(`/projects/${projectId}/artifacts/${artifactId}`, {
            method: 'PATCH',
            body: JSON.stringify(updates),
        }),

    // --- Binder Assembly (ADR-071) ---
    assembleWorkBinder: (projectId) =>
        request(`/work-binder/${projectId}/assemble`, { method: 'POST' }),

    getWorkBinder: (projectId) =>
        request(`/work-binder/${projectId}/binder`).catch(() => null),

    // --- Document Reference Resolution (ADR-071) ---
    resolveReferences: (projectId, refs) =>
        request(`/projects/${projectId}/documents/resolve?refs=${encodeURIComponent(refs.join(','))}`),

    // --- Synthesis (ADR-070) ---
    triggerSynthesis: (projectId) =>
        request(`/projects/${projectId}/synthesis`, { method: 'POST' }),

    getSynthesisDelta: (projectId) =>
        request(`/projects/${projectId}/synthesis`).catch(() => null),

    recordFindingDecision: (projectId, findingId, decision, note = null) =>
        request(`/projects/${projectId}/synthesis/findings/${findingId}`, {
            method: 'PATCH',
            body: JSON.stringify({ decision, note }),
        }),

    explainFinding: (projectId, findingId) =>
        request(`/projects/${projectId}/synthesis/findings/${findingId}/explain`, {
            method: 'POST',
        }),
};

/**
 * Create SSE connection for production events
 */
export function createProductionSSE(projectId) {
    const url = `${API_BASE}/production/events?project_id=${projectId}`;
    return new EventSource(url);
}

/**
 * Create SSE connection for intake generation events
 */
export function createIntakeSSE(executionId) {
    const url = `${API_BASE}/intake/${executionId}/events`;
    return new EventSource(url);
}

/**
 * Create SSE connection for execution progress
 */
export function createExecutionSSE(executionId) {
    const url = `/executions/${executionId}/stream`;
    return new EventSource(url);
}

/**
 * Get login URL for OAuth provider
 */
export function getLoginUrl(provider) {
    return `/auth/login/${provider}`;
}

export { ApiError, getCsrfToken };
